import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
} from "reactstrap";

const SupportStatsModal = ({ isOpen, toggle, stats, loading }) => {
  const getStateBadge = (stateValue) => {
    const stateLower = stateValue?.toLowerCase();
    switch (stateLower) {
      case "raised":
        return <Badge color="warning" className="px-2 py-1">Raised</Badge>;
      case "inprogress":
        return <Badge color="info" className="px-2 py-1">In Progress</Badge>;
      case "testing":
        return <Badge color="primary" className="px-2 py-1">Testing</Badge>;
      case "done":
        return <Badge color="success" className="px-2 py-1">Done</Badge>;
      default:
        return <Badge color="secondary" className="px-2 py-1">{stateValue || "N/A"}</Badge>;
    }
  };

  const getTypeBadge = (typeValue) => {
    switch (typeValue) {
      case "Report bug":
        return <Badge color="danger" className="px-2 py-1">Report bug</Badge>;
      case "Suggest tag":
        return <Badge color="info" className="px-2 py-1">Suggest tag</Badge>;
      case "Feedback":
        return <Badge color="success" className="px-2 py-1">Feedback</Badge>;
      case "Help":
        return <Badge color="primary" className="px-2 py-1">Help</Badge>;
      default:
        return <Badge color="secondary" className="px-2 py-1">{typeValue || "N/A"}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="bg-info text-white">
        <i className="mdi mdi-chart-bar me-2"></i>Support Statistics
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="info" />
            <p className="mt-3 text-muted">Loading statistics...</p>
          </div>
        ) : stats ? (
          <>
            {/* Total Count */}
            <Card className="shadow-sm mb-3 border-primary">
              <CardBody className="p-4">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="mb-2 fw-bold">
                      <i className="mdi mdi-file-document-multiple me-2"></i>Total Complaints
                    </h6>
                    <div className="display-4 fw-bold text-primary">
                      {stats.total_count || 0}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* By State */}
            {stats.by_state && stats.by_state.length > 0 && (
              <Card className="shadow-sm mb-3">
                <CardBody className="p-4">
                  <h6 className="mb-3 fw-bold">
                    <i className="mdi mdi-state-machine me-2"></i>By State
                  </h6>
                  <Row className="g-3">
                    {stats.by_state.map((item, index) => (
                      <Col md={6} key={index}>
                        <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded border">
                          <div>
                            {getStateBadge(item.state)}
                          </div>
                          <div className="fw-bold fs-5">
                            {item.count || 0}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* By Category */}
            {stats.by_category && stats.by_category.length > 0 && (
              <Card className="shadow-sm mb-3">
                <CardBody className="p-4">
                  <h6 className="mb-3 fw-bold">
                    <i className="mdi mdi-tag-multiple me-2"></i>By Category
                  </h6>
                  <Row className="g-3">
                    {stats.by_category.map((item, index) => (
                      <Col md={6} key={index}>
                        <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded border">
                          <div className="flex-grow-1">
                            <div className="small text-muted mb-1">Category</div>
                            <div className="fw-semibold">{item.category || "Not Defined"}</div>
                          </div>
                          <div className="fw-bold fs-5 ms-3">
                            {item.count || 0}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* By Type */}
            {stats.by_type && stats.by_type.length > 0 && (
              <Card className="shadow-sm mb-3">
                <CardBody className="p-4">
                  <h6 className="mb-3 fw-bold">
                    <i className="mdi mdi-format-list-bulleted-type me-2"></i>By Type
                  </h6>
                  <Row className="g-3">
                    {stats.by_type.map((item, index) => (
                      <Col md={6} key={index}>
                        <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded border">
                          <div>
                            {getTypeBadge(item.type)}
                          </div>
                          <div className="fw-bold fs-5">
                            {item.count || 0}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* Date Filter Info */}
            {stats.date_filter && (
              <Card className="shadow-sm">
                <CardBody className="p-4">
                  <h6 className="mb-3 fw-bold">
                    <i className="mdi mdi-calendar-filter me-2"></i>Date Filter Applied
                  </h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Date Column</label>
                        <div className="fw-semibold">{stats.date_filter.column || "N/A"}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Start Date</label>
                        <div className="small">{formatDate(stats.date_filter.start_date)}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">End Date</label>
                        <div className="small">{formatDate(stats.date_filter.end_date)}</div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <i className="mdi mdi-information-outline" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
            <p className="mt-3 text-muted">No statistics available</p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <i className="mdi mdi-close me-2"></i>Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SupportStatsModal;

